import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import Location from "../../hooks/Location.js";
import useLogRecordOpen from "../../hooks/useLogRecordOpen";
import { db } from "../../firebase/config.js";
import { useFirestoreGeneral } from "../../firebase/useFirestore.js";
import { buildStreetLabelMap, phoneLabel } from "../../helpers/adressHelpers/buildStreetLabels.js";
import { SavedIcon } from "../../StateManagment/SavedIcon.js";
import { Auth } from "../../StateManagment/Auth.js";
import SelectableGrid from "../../components/common/SelectableGrid.jsx";
import CallLink from "../../components/mobile/CallLink.jsx";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard.jsx";
import AdressStatusSelect from "../../components/adresses/AdressStatusSelect.jsx";
import resolveAdressLabels, { REPLAN_LABEL_KEY, hasLabel, labelPatch } from "../../helpers/adressHelpers/resolveAdressLabels.js";
import { statusDisplaysFrom } from "../../hooks/useStatusDisplay";
import Data from "../../components/adresses/data/Data.jsx";
import Notes from "../../components/adresses/notes/Notes.jsx";
import Materialen from "../../components/adresses/materials/Materialen.jsx";
import ContactMoments from "../../components/adresses/contactMoments/ContactMoments.jsx";
import useSettings from "../../hooks/Settings.jsx";
import NotesAttachments from "../../components/personas/reports/NotesAttachments.jsx";
import HouseholdResearchLinks from "../../components/adresses/research/HouseholdResearchLinks.jsx";
import HouseholdNextMomentNote from "../../components/adresses/research/HouseholdNextMomentNote.jsx";
import { MeasurementSignalsProvider } from "../../StateManagment/MeasurementSignalsContext.jsx";
import getAdressDescription from "../../helpers/getAdressDescription.js";
import recordStatusChange from "../../helpers/statusHistory/recordStatusChange.js";
import { mayClearSelfPickup } from "../../helpers/adressHelpers/selfPickup.js";

const TABS = [
  { id: "data", label: "Gegevens" },
  { id: "notes", label: "Notities" },
  { id: "photos", label: "Foto's" },
  { id: "materials", label: "Materialen" },
  { id: "contactMoments", label: "Contactmomenten" },
  { id: "research", label: "Vragenlijst" },
];

/**
 * Mobile detail page for one household the user is assigned to.
 *
 * Deliberately planning-free: no agenda, no afspraken, no herhaling. It reuses
 * the exact same tab components as the desktop address page, so every edit
 * lands in the same collections. The reused components read the address id
 * from `Location()[3]`, which is why this route keeps the
 * `/:orgId/<page>/:id` shape.
 *
 * Het telefoonnummer staat onder het adres en niet alleen op het tabblad
 * Gegevens: voor een dichte deur telt wat meteen zichtbaar is, en de fixer
 * heeft onderweg alleen zijn telefoon bij zich.
 *
 * Het tabblad Vragenlijst toont de QR-code hier op het scherm in plaats van op
 * de printbare QR-pagina: die route bestaat niet in de mobiele navigatie, en
 * aan de deur is er niets te printen. Zonder dit tabblad is er op de telefoon
 * geen enkele manier om een nulmeting te starten.
 *
 * Boven de links staat welk meetmoment het eerstvolgende is en wanneer (zes
 * weken na de vorige meting). De signalering leest daarvoor alleen dít
 * huishouden, niet de hele organisatie — vandaar de provider met `entityId`.
 */
const MobileHouseholdDetail = () => {
  // Context
  const [, setSaved] = useContext(SavedIcon);
  const [auth] = useContext(Auth);

  // Wachttijd voor 'Onbereikbaar'. `loaded` afdwingen: tot dat moment staat de
  // waarde nog op de standaard van 14, en zou de knop bij een tenant met 30 of
  // 60 dagen te vroeg verschijnen.
  const { unreachableAfterDays, adressStatusConfig, adressLabels, loaded: settingsLoaded } = useSettings();
  // De naam die de organisatie het label 'Opnieuw inplannen' gaf; zonder
  // instelling de ingebouwde naam.
  const replanLabel = (adressLabels || resolveAdressLabels({})).find((l) => l.key === REPLAN_LABEL_KEY);

  // State
  const [activeTab, setActiveTab] = useState("data");
  const [adressDescription, setAdressDescription] = useState("");

  // Hooks
  const history = useHistory();
  const { orgId } = useParams();
  const adressId = Location()[3];
  // Auditlog: de mobiele route is een eigen component, dus hij heeft zijn eigen
  // event nodig. Zie docs/features/clientdossier-auditlog.md.
  useLogRecordOpen('household', adressId);

  // Firestore
  // Eigen deelnemer: huishoudens hangen aan een deelnemer en niet aan een
  // inlogaccount. Alleen nodig om te weten of dit huishouden van hem is.
  const myPersonas = useFirestoreGeneral("Personas", "UserID", auth?.ID || "");
  const myPersonaId = (Array.isArray(myPersonas) ? myPersonas[0]?.ID : "") || "";

  const adress = useFirestoreGeneral("Adresses", "ID", adressId || "");
  const adressDoc = Array.isArray(adress) ? adress[0] : null;
  // Meeliften op de useSettings hierboven: een eigen useStatusDisplays()
  // zou een tweede stel listeners op hetzelfde document opzetten.
  const statusDisplay = useMemo(
    () => statusDisplaysFrom({ adressStatusConfig }).adress,
    [adressStatusConfig],
  );

  // Het telefoonnummer bovenaan: vast gegeven op het huishouden, tot de
  // migratie aangevuld uit de rijen van het tabblad Gegevens (T10).
  // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md
  const adressData = useFirestoreGeneral("PersonaData", "PersonaID", adressId || "");
  const phone = useMemo(
    () => phoneLabel(buildStreetLabelMap(adressData, Array.isArray(adress) ? adress : []), adressId, ""),
    [adressData, adress, adressId],
  );

  // Direct read (not a hook) — getAdressDescription scopes by CompagnyID.
  useEffect(() => {
    if (!adressId) return;
    let cancelled = false;
    getAdressDescription(adressId).then((description) => {
      if (!cancelled) setAdressDescription(description);
    });
    return () => { cancelled = true; };
  }, [adressId]);

  /**
   * De klusser zet het vinkje 'Zelf op te pakken' uit: het bleek toch geen klus
   * die je zomaar oppakt.
   *
   * Dit verandert niets aan nu — hij staat op dit huishouden, dus het staat al
   * niet meer in de lijst. Het werkt pas als die toewijzing vervalt: dan komt
   * het niet meer vanzelf terug. Voor de planner staat er in haar overzicht
   * 'toegewezen aan hem, vinkje uit', en dat vertelt het hele verhaal.
   *
   * @returns {Promise<void>}
   */
  const clearSelfPickupHandler = async () => {
    if (!adressDoc?.docid) return;

    await db.collection("Adresses").doc(adressDoc.docid).update({ SelfPickup: "false" });

    setSaved("flex");
  };

  /** Het label 'Opnieuw inplannen' aan- of uitzetten. */
  const replanHandler = async () => {
    if (!adressDoc?.docid) return;
    const aan = !hasLabel(adressDoc, REPLAN_LABEL_KEY);
    await db.collection("Adresses").doc(adressDoc.docid).update(labelPatch(adressDoc, REPLAN_LABEL_KEY, aan));
    setSaved("flex");
  };

  const statusHandler = async (e) => {
    if (!adressDoc?.docid) return;
    const value = e.target.options[e.target.selectedIndex].value;
    const previousStatus = adressDoc.Status;

    await db.collection("Adresses").doc(adressDoc.docid).update({ Status: value });

    await recordStatusChange({
      entityType: "adress",
      entityID: adressDoc.ID,
      fromStatus: previousStatus,
      toStatus: value,
      changedBy: auth?.ID,
    });

    setSaved("flex");
  };

  return (
    <div className="mobile-page">
      <button
        className="mobile-back-button"
        onClick={() => history.push(`/${orgId}/myhouseholds`)}
      >
        <ArrowBackOutlinedIcon />
        Mijn huishoudens
      </button>

      <h2 className="mobile-page-heading">{adressDescription || "Huishouden"}</h2>

      <CallLink phone={phone} label={adressDescription || "dit huishouden"} />

      {adressDoc && (
        <ReadOnlyGuard edit="addresses.edit">
          <div className="mobile-household-status">
            <label htmlFor="mobile-household-status-select">Status huishouden</label>
            <AdressStatusSelect
              value={adressDoc.Status}
              display={statusDisplay}
              docid={adressDoc.docid}
              onChange={statusHandler}
            />
          </div>
        </ReadOnlyGuard>
      )}

      {/* Een klusser mag niet altijd zelf inplannen; met dit label vraagt hij
          de planner om een nieuwe afspraak. Het huishouden komt dan in de lijst
          'Inplannen', en het label gaat vanzelf weg zodra er een afspraak staat.
          AI-CONTEXT: See docs/features/planner-overzicht-bkb.md */}
      {adressDoc && replanLabel && (
        <ReadOnlyGuard edit="addresses.edit">
          <button
            type="button"
            className={`mobile-replan-button${hasLabel(adressDoc, REPLAN_LABEL_KEY) ? ' mobile-replan-button--on' : ''}`}
            aria-pressed={hasLabel(adressDoc, REPLAN_LABEL_KEY)}
            onClick={replanHandler}
          >
            {hasLabel(adressDoc, REPLAN_LABEL_KEY)
              ? `${replanLabel.label}: aangevraagd (tik om in te trekken)`
              : `${replanLabel.label} aanvragen`}
          </button>
        </ReadOnlyGuard>
      )}

      {/* Alleen op een huishouden dat hij zelf heeft opgepakt. Kon hij het
          uitzetten terwijl het nog vrij stond, dan verdween het stil uit ieders
          lijst zonder dat er iemand aan werkt.
          AI-CONTEXT: See docs/features/huishouden-zelf-op-te-pakken.md */}
      {mayClearSelfPickup(adressDoc, myPersonaId) && (
        <ReadOnlyGuard edit="addresses.edit">
          <button className="mobile-clear-pickup-button" onClick={clearSelfPickupHandler}>
            Toch niet zelf op te pakken
          </button>
        </ReadOnlyGuard>
      )}

      <SelectableGrid
        items={TABS}
        getId={(item) => item.id}
        getLabel={(item) => item.label}
        selectedId={activeTab}
        onClick={setActiveTab}
      />

      <Data adressNavigation={activeTab} />
      <Notes adressNavigation={activeTab} />
      <div style={{ display: activeTab === "photos" ? "block" : "none" }}>
        <NotesAttachments
          parentId={adressId}
          parentType="adress"
          title="Foto's en documenten"
          editPermission="addresses.edit"
        />
      </div>
      <Materialen adressNavigation={activeTab} />
      {/* Verbergen, niet weglaten: het gekozen meetmoment en de taal leven in
          HouseholdResearchLinks zelf, en die keuze mag niet wegvallen als de
          fixer even naar een ander tabblad kijkt.
          AI-CONTEXT: See docs/pitfalls.md#ui-filtering-a-list-unmounts-unsaved-input */}
      <div className={activeTab === "research" ? "mobile-tab" : "mobile-tab-hidden"}>
        <MeasurementSignalsProvider entityId={adressId} entityKind="adress" enabled={Boolean(adressId)}>
          <HouseholdNextMomentNote adressID={adressId} />
        </MeasurementSignalsProvider>
        <HouseholdResearchLinks adressID={adressId} qrMode="inline" />
      </div>
      <ContactMoments
        adressNavigation={activeTab}
        adressDocid={adressDoc?.docid || ""}
        adressStatus={adressDoc?.Status || ""}
        unreachableAfterDays={settingsLoaded ? unreachableAfterDays : null}
      />
    </div>
  );
};

export default MobileHouseholdDetail;
